import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, UserPlus, Gift } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteValid, setInviteValid] = useState(false);

  useEffect(() => {
    // Check for invite code in URL
    const invite = searchParams.get('invite');
    if (invite) {
      setInviteCode(invite);
      validateInvite(invite);
    }
  }, [searchParams]);

  const validateInvite = async (code) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/invites/validate/${code}`
      );
      const data = await response.json();
      if (data.valid) {
        setInviteValid(true);
        toast.success('Valid invite code! You\'ll get free lifetime access.');
      }
    } catch (error) {
      console.error('Error validating invite:', error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const userCredential = await signup(email, password);
      
      // If invite code, redeem it
      if (inviteCode && userCredential.user) {
        try {
          // Get the ID token directly from the user credential
          const token = await userCredential.user.getIdToken();
          
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/invites/redeem/${inviteCode}`,
            {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          
          const data = await response.json();
          
          if (response.ok && data.success) {
            toast.success('🎉 Account created with free lifetime access!');
          } else {
            console.error('Failed to redeem invite:', data);
            toast.success('Account created successfully!');
          }
        } catch (inviteError) {
          console.error('Error redeeming invite:', inviteError);
          toast.success('Account created successfully! Please contact support to activate your invite.');
        }
      } else {
        toast.success('Account created successfully!');
      }
      
      // Small delay to ensure premium status is updated before navigating
      await new Promise(resolve => setTimeout(resolve, 1000));
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'Registration failed';
      
      // Handle Firebase auth errors
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Use at least 6 characters';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md" style={{ background: 'var(--bg-card)', backdropFilter: 'blur(10px)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
        <CardHeader className="space-y-2 text-center">
          <h1 className="text-4xl font-bold" 
              style={{ 
                fontFamily: 'Work Sans', 
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-green) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
            CalmGuard
          </h1>
          <p className="text-base mb-6" style={{ color: 'var(--text-secondary)', fontFamily: 'Work Sans' }}>
            Tools for Navigating Narcissism
          </p>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-4 mt-2" 
               style={{ background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-green) 100%)' }}>
            <UserPlus className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Work Sans', color: 'var(--text-primary)' }}>
            Create Account
          </CardTitle>
          <CardDescription style={{ color: 'var(--text-secondary)' }}>
            {inviteValid ? 'Sign up to get free lifetime access!' : 'Sign up to save and sync your plans across devices'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Invite Banner */}
          {inviteValid && (
            <div className="p-3 rounded-lg flex items-center gap-2" style={{ background: 'rgba(16, 185, 129, 0.1)', border: '2px solid var(--primary-green)' }}>
              <Gift className="w-5 h-5" style={{ color: 'var(--primary-green)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--primary-green)' }}>
                🎉 Free Lifetime Access with this invite!
              </span>
            </div>
          )}
          
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: 'var(--text-primary)' }}>Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: 'var(--text-primary)' }}>Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="At least 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" style={{ color: 'var(--text-primary)' }}>Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11"
              disabled={loading}
              style={{ 
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-green) 100%)',
                color: 'white'
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="font-medium hover:underline"
              style={{ color: 'var(--primary-blue)' }}
            >
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
